import {
  ChangeDetectionStrategy,
  Component,
  Inject,
  signal,
} from '@angular/core';
import { DIALOG_DATA } from '@angular/cdk/dialog';
import { TagsApi } from '@app/tags/tag-api.model';
import { UserProfileSummary, UserSearchItem } from '@app/user/user-api.model';
import { TranslateService } from '@ngx-translate/core';
import { TypeaheadSearchFunction } from '@app/shared/shared-api.model';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  Observable,
  of,
  switchMap,
} from 'rxjs';
import { UserService } from '@app/user/services/user.service';
import { UserSearchV3Component } from '@app/user/components/user-search-v3/user-search-v3.component';
import { UserSearchItemV3Component } from '@app/user/components/user-search-v3/user-search-item-v3/user-search-item-v3.component';
import { TagsService } from '@app/tags/services/tags.service';
import { InternalTagRatingTypes } from '@app/tags/tags';
import { DialogService } from '@degreed/apollo-angular';
import { TagModalService } from '@app/tags/services/tag-modal.service';

@Component({
  selector: 'dgx-tag-manager-rating-request-modal-v3',
  standalone: true,
  imports: [UserSearchV3Component, UserSearchItemV3Component],
  templateUrl: './tag-manager-rating-request-modal-v3.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TagManagerRatingRequestModalV3Component {
  public i18n = this.translateService.instant([
    'RequestRating_Header',
    'RequestRating_SendRequest',
    'TargetResourcesForm_Selected',
    'Core_Cancel',
    'Core_SearchForManagerLabel',
  ]);
  public requestedManager = signal<UserSearchItem>(null);
  public isRequestProcessing = signal<boolean>(false);

  constructor(
    @Inject(DIALOG_DATA)
    public data: {
      tag: TagsApi.TagDetails;
      manager: UserProfileSummary;
    },
    private translateService: TranslateService,
    private userService: UserService,
    private tagsService: TagsService,
    private dialogService: DialogService,
    private tagModalService: TagModalService
  ) {
    this.requestedManager.set(data.manager);
  }

  // function to pass the user search component
  public loadPotentialRaters: TypeaheadSearchFunction<string, UserSearchItem> =
    (term: Observable<string>): Observable<readonly UserSearchItem[]> => {
      return term.pipe(
        debounceTime(300),
        distinctUntilChanged(),
        filter((term) => term.length >= 2),
        switchMap((term: string) => {
          return this.userService.findNetworkMembers(term, true, 20);
        }),
        catchError(() => {
          return of([] as UserSearchItem[]);
        })
      );
    };

  public onSelectItem(item: UserSearchItem): void {
    this.requestedManager.set(item);
  }

  public onRemoveItem(): void {
    this.requestedManager.set(null);
  }

  //when request is submitted
  public submit(): void {
    this.isRequestProcessing.set(true);
    this.tagsService
      .requestTagRating(
        this.data.tag.tagId,
        InternalTagRatingTypes.manager,
        this.requestedManager().userProfileKey
      )
      .subscribe({
        next: (response) => {
          const data = { ...this.requestedManager(), ...response };
          // refresh the list view
          this.tagsService.notifyUserTagsModified();
          this.openPreviousDialog();
          this.dialogService.close(
            TagManagerRatingRequestModalV3Component,
            data
          );
        },
        complete: () => {
          this.isRequestProcessing.set(false);
        },
      });
  }
  public cancel(): void {
    this.openPreviousDialog();
    this.dialogService.close(TagManagerRatingRequestModalV3Component);
  }

  private openPreviousDialog(): void {
    this.tagModalService.openPreviousDialog();
  }
}
